import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import CountUp from "react-countup";
import { ArrowRight, ShieldCheck, Lock, Zap, CheckCircle2, Sparkles } from "lucide-react";
import api, { formatCurrency, COUNTRY_FLAGS } from "@/lib/api";
import Seo from "@/components/Seo";

const SITE_ORIGIN =
  typeof window !== "undefined" ? window.location.origin : "https://giftopay.com";

const BRAND_THEMES = {
  amazon: ["#232F3E", "#FF9900"], walmart: ["#0071CE", "#FFC220"], target: ["#CC0000", "#7C0000"],
  bestbuy: ["#0046BE", "#FFE000"], ebay: ["#E53238", "#0064D2"], macys: ["#E21A2C", "#000000"],
  nike: ["#111111", "#FA5400"], steam: ["#1B2838", "#66C0F4"], apple: ["#0B3C5D", "#1d1d1f"],
  googleplay: ["#34A853", "#1A6334"], visa: ["#1A1F71", "#0E1248"], mastercard: ["#EB001B", "#F79E1B"],
  starbucks: ["#00704A", "#003B25"], playstation: ["#003791", "#0070D1"], xbox: ["#107C10", "#0E5C0E"],
  sephora: ["#000000", "#444444"], airbnb: ["#FF5A5F", "#E11D48"], uber: ["#000000", "#222222"],
};

export default function BrandLanding() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [brands, setBrands] = useState([]);
  const [countries, setCountries] = useState([]);
  const [country, setCountry] = useState("US");
  const [amount, setAmount] = useState(100);
  const [quote, setQuote] = useState(null);

  useEffect(() => {
    api.get("/catalog/brands").then((r) => setBrands(r.data));
    api.get("/catalog/countries").then((r) => setCountries(r.data));
  }, []);

  const brand = useMemo(() => brands.find((b) => b.id === slug), [brands, slug]);

  useEffect(() => {
    if (!brand) return;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/rates/quote", {
          brand_id: brand.id, country, amount: Number(amount),
        });
        setQuote(data);
      } catch { /* ignore */ }
    }, 200);
    return () => clearTimeout(t);
  }, [brand, country, amount]);

  if (brands.length && !brand) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-center px-4" data-testid="brand-not-found">
        <div>
          <h1 className="font-display text-3xl font-bold text-[#0B3C5D]">Brand not found</h1>
          <p className="mt-2 text-slate-600 text-sm">
            We don&apos;t support that brand yet.{" "}
            <Link to="/" className="underline text-[#00C853]">See all supported brands</Link>.
          </p>
        </div>
      </div>
    );
  }
  if (!brand) return <div className="min-h-[60vh]" />;

  const [c1, c2] = BRAND_THEMES[brand.id] || ["#0B3C5D", "#164e75"];
  const topCountryEntry = Object.entries(brand.rate).sort((a, b) => b[1] - a[1])[0];
  const topRate = Math.round(topCountryEntry[1] * 100);
  const currentCountry = countries.find((c) => c.code === country);

  const title = `Sell ${brand.name} Gift Cards`;
  const description = `Sell your ${brand.name} gift card on Giftopay for up to ${topRate}% — instant quotes, reviewed payouts via PayPal, Crypto, Bank Transfer. US · UK · UAE · CA.`;
  const canonical = `${SITE_ORIGIN}/sell/${brand.id}`;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": `Sell ${brand.name} Gift Cards on Giftopay`,
    "description": description,
    "areaServed": ["United States", "United Kingdom", "United Arab Emirates", "Canada"],
    "provider": {
      "@type": "Organization",
      "name": "Giftopay",
      "url": SITE_ORIGIN,
    },
    "offers": Object.entries(brand.rate).map(([cc, r]) => ({
      "@type": "Offer",
      "name": `${brand.name} (${cc})`,
      "price": (r * 100).toFixed(1),
      "priceCurrency": (countries.find((c) => c.code === cc) || {}).currency || "USD",
    })),
  };

  return (
    <div className="min-h-screen" data-testid="brand-landing">
      <Seo title={title} description={description} canonical={canonical} jsonLd={jsonLd} />

      <section className="relative overflow-hidden bg-gradient-to-br from-[#F8FAFC] to-white pt-16 pb-12">
        <div className="absolute inset-0 grid-pattern opacity-40 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <Link to="/" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-[#0B3C5D]" data-testid="brand-breadcrumb">
              ← All brands
            </Link>
            <h1 className="font-display mt-3 text-4xl sm:text-5xl lg:text-6xl font-bold text-[#0B3C5D] leading-[1.05] tracking-tight">
              Sell <span className="gradient-text">{brand.name}</span><br className="hidden sm:block" /> gift cards for cash.
            </h1>
            <p className="mt-5 text-lg text-slate-600 max-w-xl">
              Get up to <strong className="text-[#0B3C5D]">{topRate}%</strong> for your {brand.name} card. Quote in seconds, payout within 60 minutes to 24 hours via PayPal, Crypto, Bank Transfer, Cash App and more.
            </p>
            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <Link
                to={`/sell?brand=${brand.id}&country=${country}`}
                className="inline-flex items-center justify-center rounded-full bg-[#0B3C5D] text-white px-7 py-3.5 text-base font-semibold btn-primary"
                data-testid="brand-cta-sell"
              >
                Sell {brand.name} Now <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
              <Link
                to="/rates"
                className="inline-flex items-center justify-center rounded-full bg-white border border-slate-200 text-[#0B3C5D] px-7 py-3.5 text-base font-semibold hover:border-[#0B3C5D]"
                data-testid="brand-cta-rates"
              >
                See all rates
              </Link>
            </div>
            <div className="mt-7 flex flex-wrap items-center gap-5 text-xs text-slate-600">
              <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-[#00C853]" /> Reviewed Payouts</span>
              <span className="inline-flex items-center gap-1.5"><Lock className="w-4 h-4 text-[#00C853]" /> 256-bit SSL</span>
              <span className="inline-flex items-center gap-1.5"><Zap className="w-4 h-4 text-[#00C853]" /> Paid in 1–24h</span>
            </div>
          </div>

          {/* Visual gift card */}
          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="absolute -inset-3 bg-gradient-to-br from-[#0B3C5D]/10 to-[#00C853]/10 rounded-3xl blur-xl" />
              <div
                className="relative aspect-[1.586/1] rounded-3xl overflow-hidden shadow-2xl"
                style={{ background: `linear-gradient(135deg, ${c1} 0%, ${c2} 100%)` }}
              >
                <div className="absolute inset-0 bg-gradient-to-tr from-white/0 via-white/8 to-white/15 pointer-events-none" />
                <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-white opacity-25 blur-2xl" />
                <div className="absolute top-5 left-5 w-12 h-9 rounded-md bg-gradient-to-br from-yellow-200 to-yellow-500 border border-yellow-700/30 shadow-inner" />
                <div className="absolute top-5 right-5 px-3 py-1 rounded-full bg-white/15 backdrop-blur-sm text-xs font-semibold text-white border border-white/20">
                  up to {topRate}%
                </div>
                <div className="absolute bottom-5 left-5 right-5">
                  <p className="text-[11px] uppercase tracking-[0.18em] text-white/60">Gift Card</p>
                  <p className="font-display text-2xl sm:text-3xl font-bold text-white mt-1">{brand.name}</p>
                </div>
                <Sparkles className="absolute bottom-5 right-5 w-5 h-5 text-white/40" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Inline calculator */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs uppercase tracking-[0.2em] text-[#00C853] font-semibold text-center">{brand.name} Rate Calculator</p>
          <h2 className="text-center font-display text-3xl sm:text-4xl font-bold text-[#0B3C5D] mt-3 tracking-tight">
            What&apos;s your {brand.name} card worth?
          </h2>

          <div className="mt-10 grid lg:grid-cols-2 gap-8">
            <div className="rounded-3xl border border-slate-200 p-6 sm:p-8">
              <label className="text-xs font-medium uppercase tracking-wider text-slate-500">Country</label>
              <div className="mt-2 grid grid-cols-4 gap-2">
                {countries.map((c) => (
                  <button
                    key={c.code}
                    onClick={() => setCountry(c.code)}
                    className={`rounded-xl border px-3 py-2.5 text-sm font-semibold transition ${
                      country === c.code ? "border-[#0B3C5D] bg-[#0B3C5D] text-white" : "border-slate-200 text-slate-700 hover:border-[#0B3C5D]"
                    }`}
                    data-testid={`brand-country-${c.code}`}
                  >
                    <span className="mr-1">{COUNTRY_FLAGS[c.code]}</span>{c.code}
                  </button>
                ))}
              </div>
              <label className="text-xs font-medium uppercase tracking-wider text-slate-500 mt-6 block">
                Card Value ({currentCountry?.currency || "USD"})
              </label>
              <input
                type="number" min="10" step="5" value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-2xl font-display font-bold text-[#0B3C5D] focus:outline-none focus:border-[#0B3C5D] focus:ring-4 focus:ring-[#0B3C5D]/10"
                data-testid="brand-amount"
              />
              <div className="mt-3 flex flex-wrap gap-2">
                {[50, 100, 250, 500, 1000].map((v) => (
                  <button key={v} onClick={() => setAmount(v)}
                    className="px-3 py-1 rounded-full text-xs font-medium border border-slate-200 text-slate-600 hover:border-[#0B3C5D]"
                    data-testid={`brand-preset-${v}`}>
                    {v}
                  </button>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="absolute -inset-2 bg-gradient-to-br from-[#0B3C5D]/8 to-[#00C853]/8 rounded-3xl blur-xl" />
              <div className="relative rounded-3xl bg-navy-gradient text-white p-6 sm:p-8">
                <p className="text-xs uppercase tracking-[0.2em] text-white/70">Your Payout</p>
                <p className="font-display text-5xl font-bold mt-2 tracking-tight">
                  {quote ? (
                    <>
                      <span className="text-[#00C853]">{quote.currency === "USD" ? "$" : quote.currency === "GBP" ? "£" : quote.currency === "CAD" ? "C$" : "د.إ"}</span>
                      <CountUp end={quote.final_payout} duration={0.6} decimals={2} separator="," preserveValue />
                    </>
                  ) : "—"}
                </p>
                <div className="mt-5 space-y-2 text-sm">
                  <Row label="Rate" value={quote ? `${(quote.rate_pct * 100).toFixed(1)}%` : "—"} />
                  <Row label="Gross" value={quote ? formatCurrency(quote.gross_payout, quote.currency) : "—"} />
                  <Row label="Fee" value={quote ? `- ${formatCurrency(quote.processing_fee, quote.currency)}` : "—"} />
                </div>
                <button
                  onClick={() => navigate(`/sell?brand=${brand.id}&country=${country}&amount=${amount}`)}
                  className="mt-6 inline-flex items-center justify-center w-full rounded-full bg-[#00C853] text-white py-3.5 font-semibold hover:bg-[#00a543]"
                  data-testid="brand-cta-locked"
                >
                  Lock & sell <ArrowRight className="ml-2 w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Per-country rate table */}
      <section className="py-16 bg-[#F8FAFC]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#0B3C5D] tracking-tight">
            {brand.name} gift card rates by country
          </h2>
          <p className="mt-2 text-sm text-slate-600">
            Rates may vary slightly based on demand and card condition.
          </p>

          <div className="mt-6 bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500">
                <tr className="text-left">
                  <th className="px-6 py-3 font-medium">Country</th>
                  <th className="px-6 py-3 font-medium">Rate</th>
                  <th className="px-6 py-3 font-medium text-right">Sell now</th>
                </tr>
              </thead>
              <tbody>
                {countries.map((c) => (
                  <tr key={c.code} className="border-t border-slate-100">
                    <td className="px-6 py-3 font-medium">{COUNTRY_FLAGS[c.code]} {c.name}</td>
                    <td className="px-6 py-3 font-display font-bold text-[#00C853]">
                      {(brand.rate[c.code] * 100).toFixed(1)}%
                    </td>
                    <td className="px-6 py-3 text-right">
                      <Link
                        to={`/sell?brand=${brand.id}&country=${c.code}`}
                        className="text-xs font-semibold text-[#0B3C5D] hover:text-[#00C853]"
                        data-testid={`brand-row-cta-${c.code}`}
                      >
                        Start <ArrowRight className="w-3 h-3 inline" />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* SEO-friendly Q&A */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#0B3C5D] tracking-tight">
            How to sell {brand.name} gift cards on Giftopay
          </h2>
          <ol className="mt-6 space-y-4 text-sm text-slate-700">
            {[
              `Pick "${brand.name}" in our sell wizard and enter the card value in your local currency.`,
              `Review your instant quote. The rate is locked for 5 minutes once you submit.`,
              `Enter your card number, PIN (if any), and choose a payout method (PayPal, Cash App, Bank, Crypto, Interac, USDT).`,
              `We verify the balance, run risk checks, and release payment — typically within 60 minutes to 24 hours.`,
            ].map((step, i) => (
              <li key={i} className="flex gap-3">
                <span className="shrink-0 w-7 h-7 rounded-full bg-[#00C853] text-white text-xs font-bold flex items-center justify-center">{i + 1}</span>
                <span>{step}</span>
              </li>
            ))}
          </ol>

          <div className="mt-10 rounded-2xl bg-slate-50 border border-slate-200 p-6 flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-[#00C853] shrink-0 mt-0.5" />
            <p className="text-sm text-slate-700">
              <strong className="text-[#0B3C5D]">Tip:</strong> {brand.name} cards in our {brand.category} category typically sell fastest when submitted as <strong>digital (e-gift)</strong> with a clear PIN. Used cards with partial balance must include the remaining balance amount.
            </p>
          </div>

          <div className="mt-8 text-center">
            <Link
              to={`/sell?brand=${brand.id}&country=${country}`}
              className="inline-flex items-center justify-center rounded-full bg-[#0B3C5D] text-white px-7 py-3.5 text-base font-semibold btn-primary"
              data-testid="brand-bottom-cta"
            >
              Sell my {brand.name} card <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between text-white/80">
      <span>{label}</span>
      <span className="font-medium text-white">{value}</span>
    </div>
  );
}
